import { faHistory } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React, { FC, Ref } from 'react'
import { useDrag } from 'react-dnd'
import { useDispatch } from 'react-redux'
import { WithTooltip } from 'web/components/generic/WithTooltip'
import { WowheadLink } from 'web/components/generic/WowheadLink'
import { getCurrentBuild } from 'web/data/core-data'
import { CLASS_META } from 'web/data/general/id'
import { FightPlayer, FightSetup, FullFight, Spec, SpecRole, Spell } from 'web/data/models'
import { getPlayerSpec, getPlayerSpells, SpellEngine } from 'web/engine/spell-engine'
import { InteractivityActions } from 'web/redux/slices/interactivity-slice'
import { DndType, makeSpellDndItem } from 'web/utils/dnd-utils'
import { assertDefined } from 'web/utils/utils'
import { DivBtn } from '../../components/generic/DivBtn'
import { GameIcon } from '../../components/generic/GameIcon'
import { WrappingDiv } from '../../components/generic/WrappingDiv'
import healerRoleImg from '../../../assets/role_healer.png'
import tankRoleImg from '../../../assets/role_tank.png'
import dpsRoleImg from '../../../assets/role_dps.png'
import { SvgCross } from 'web/components/generic/SvgCross'

type Props = {
    interactive: boolean

    fightPlayer: FightPlayer

    onEditPlayer?: () => void
    onRemovePlayer?: () => void

    fullFight: FullFight
    setup: FightSetup
}

export const FightPlayerItem: React.FC<Props> = (props) => {

    const spec = getPlayerSpec(props.fightPlayer)
    const spells = getPlayerSpells(props.fightPlayer)

    const spellsList = spells.map(spell =>
        <PlayerSpellWrapper
            key={spell.id}
            interactive={props.interactive}
            player={props.fightPlayer}
            spec={spec}
            spell={spell}
            fullFight={props.fullFight}
            setup={props.setup}
        />
    )

    const role = getCurrentBuild().Spec(props.fightPlayer.specId).role

    const roles = {
        [SpecRole.Healer]: healerRoleImg,
        [SpecRole.Tank]: tankRoleImg,
        [SpecRole.Melee]: dpsRoleImg,
        [SpecRole.Ranged]: dpsRoleImg,
    }

    return (
        <div className="border">
            <div className="bg-white border-bottom d-flex justify-content-between align-items-center">
                <div
                    className={classNames(
                        `d-flex flex-grow-1 align-self-stretch align-items-center`,
                        `bg-${CLASS_META[spec.classId].shortName}-lighter`,
                    )}
                    style={{ minHeight: 35.2 }}
                >

                    <DivBtn
                        clickThrough={props.onEditPlayer == null}
                        onClick={props.onEditPlayer}
                        fillWidth
                    >
                        <div
                            className={classNames(
                                "d-flex flex-grow-1 p-2 small align-items-start",
                            )}
                        >
                            <img
                                className="mr-1"
                                style={{ height: '14px', width: '14px', opacity: 0.8 }}
                                src={roles[role]}
                                alt="Healer role icon"
                            />

                            <div style={{ lineHeight: 0.95 }}>
                                {props.fightPlayer.name}
                            </div>
                        </div>

                        {props.fightPlayer.overrides.length >= 1 &&
                            <WithTooltip place="top" fullWidth>
                                {(ttId) => (
                                    <div
                                        data-for={ttId}
                                        data-tip={"This player has cooldown overrides"}
                                        className="d-flex p-2 align-items-center"
                                    >
                                        <FontAwesomeIcon
                                            fixedWidth
                                            icon={faHistory}
                                            flip="horizontal"
                                        />
                                    </div>
                                )}
                            </WithTooltip>
                        }
                    </DivBtn>
                </div>

                {props.onRemovePlayer != null &&
                    <DivBtn onClick={props.onRemovePlayer} fillHeight>
                        <div
                            style={{ width: 40 }}
                            className="small align-items-center justify-content-center d-flex border-left"
                        >
                            <SvgCross size={16} />
                        </div>
                    </DivBtn>
                }
            </div>

            <WrappingDiv marginPx="4px" className="p-2">
                {spellsList.length <= 0 && (
                    <div className="small text-center w-100">
                        {'No spells'}
                    </div>
                )}

                {spellsList.map(el => {
                    const { key } = el
                    assertDefined(key)

                    return (
                        // This extra div makes WrappingDiv put
                        // the proper spacing between the
                        // elements
                        <div key={key}>
                            {el}
                        </div>
                    )
                })}
            </WrappingDiv>
        </div>
    )
}

const PlayerSpellWrapper: React.FC<{
    interactive: boolean
    player: FightPlayer
    spec: Spec
    spell: Spell
    fullFight: FullFight
    setup: FightSetup
}> = (props) => {

    if (props.interactive === false) {
        return (
            <WowheadLink gameData={{ ...props.spell }} disableLeftClick>
                <PlayerSpell
                    spell={props.spell}
                    spec={props.spec}
                />
            </WowheadLink>
        )
    } else {
        return (
            <DraggablePlayerSpell
                player={props.player}
                spell={props.spell}
                spec={props.spec}
                fullFight={props.fullFight}
                setup={props.setup}
            />
        )
    }

}

const DraggablePlayerSpell: FC<{
    player: FightPlayer
    spec: Spec
    spell: Spell
    fullFight: FullFight
    setup: FightSetup
}> = (props) => {

    const disp = useDispatch()

    const viableEvents = SpellEngine
        .fromStates({
            fullFight: props.fullFight,
            setupState: props.setup
        })
        .getViableEventsForSpell({
            playerId: props.player.id,
            spellId: props.spell.id
        })

    const [, dragRef] = useDrag({
        type: DndType.SpellAssignment,

        // Notice: this is what used to be `begin` previously. Meaning
        // this callback fires when the drag event starts.
        item: () => {
            disp(InteractivityActions.setAvailableEventIdsForSetup({
                setupId: props.setup.id,
                availableEventIds: viableEvents.map(e => e.id),
            }))

            const item = makeSpellDndItem({
                payload: {
                    playerId: props.player.id,
                    spellId: props.spell.id,
                    eventOffset: 0
                }
            })

            return item
        },
        end: () => {
            disp(InteractivityActions.clearAvailableEventIdsForSetup({
                setupId: props.setup.id
            }))
        },
    })

    return (
        <PlayerSpell
            dragRef={dragRef}

            spell={props.spell}
            spec={props.spec}
            // Don't include the system-created "Pull" event as a
            // "viable" event, we very rarely want to use CDs there
            fadeOut={viableEvents.filter(e => e.userCreated === true).length <= 0}
        />
    )
}

const PlayerSpell: FC<{
    dragRef?: Ref<any>

    spec: Spec
    spell: Spell
    fadeOut?: boolean
}> = (props) => {

    return (
        <div
            ref={props.dragRef}
            className={classNames(
                'd-flex align-items-stretch ',
                "small text-monospace",
                `bg-${CLASS_META[props.spec.classId].shortName}-darker`,
            )}
            style={{
                opacity: (props.fadeOut ? 0.3 : 1),
                textShadow: "0px 0px 5px #00000095",
                color: "white",
                border: "1px solid #3d3d3d",
                cursor: (props.dragRef != null) ? 'pointer' : 'default'
            }}

        >
            <GameIcon
                size="22px"
                icon_name={props.spell.icon_name}
            />
            <div
                className="d-flex align-items-center px-2"
                style={{ borderLeft: "1px solid #3d3d3d" }}
            >
                {props.spell.shortName}
            </div>
        </div>
    )
}
